import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress
} from '@mui/material';
import Banner from '../components/Banner';
import BannerHub from '../components/BannerHub';
import CarouselArticulos from './../components/CarouselArticulos';
import NewsletterForm from './../components/NewsletterForm';
import AllArticles from './../components/AllArticle';
import { fetchAllBlogPosts } from './../components/wordpressUtils';
import FeaturedPost from '../components/FeaturedPost';
import { Helmet } from 'react-helmet';
export default function Insights() {
  const blurRef = useRef(null);
  const [wordpressArticles, setWordpressArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('TODOS');
  const [sortOrder, setSortOrder] = useState('MÁS RECIENTES');
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const seccionArticulosRef = useRef(null);

  const allArticles = React.useMemo(() => {
    return [...wordpressArticles];
  }, [wordpressArticles]);

  const filteredArticles = React.useMemo(() => {
    return allArticles
      .filter(article => {
        const categoria = Array.isArray(article.categoria)
          ? article.categoria.join(', ')
          : article.categoria;

        if (filterCategory !== 'TODOS' && !categoria.includes(filterCategory)) {
          return false;
        }

        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          return (
            article.titulo.toLowerCase().includes(searchLower) ||
            article.descripcion.toLowerCase().includes(searchLower) ||
            categoria.toLowerCase().includes(searchLower)
          );
        }
        return true;
      })
      .sort((a, b) => {
        return sortOrder === 'MÁS RECIENTES'
          ? new Date(b.fecha) - new Date(a.fecha)
          : new Date(a.fecha) - new Date(b.fecha);
      });
  }, [allArticles, filterCategory, searchTerm, sortOrder]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingArticles(true);
        const wpArticles = await fetchAllBlogPosts();
        setWordpressArticles(wpArticles);
      } catch (err) {
        console.error('Error al obtener artículos:', err);
      } finally {
        setIsLoadingArticles(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Insights | EBM Consultores</title>
        <meta
          name="description"
          content="Consultoría especializada en Empresa Familiar, Gobierno Corporativo y Legado Patrimonial. Descubre cómo acompañamos tu evolución."
        />
      </Helmet>
      <Container sx={{
        margin: 0,
        padding: '0 !important',
        maxWidth: '100vw !important',
        overflowX: 'hidden',
        backgroundColor: '#1F1F1F'
      }}>
        {/* Banner principal */}
        <Banner
          backgroundImage="https://res.cloudinary.com/dhoyyqszi/image/upload/v1750193644/ebm/room-used-official-event_vyasom.png"
          title={
            <Box sx={{
              color: 'white',
              padding: '0px 20px',
              borderRadius: '5px',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              fontSize: { xs: '24px', sm: '30px', md: '50px' },
              maxWidth: { sm: '700px', md: '937px' },
              textShadow: '0px 2px 10px rgb(0,0,0,0.5)',
            }}>
              <Box component="span" sx={{
                position: 'relative',
                px: '2vw',
                py: '0.3em',
                width: { xs: '90%', sm: '534px' },
                height: { xs: '60px', sm: '65px' },
                margin: '0 auto',
                borderRadius: '50rem',
                background: 'linear-gradient(rgba(10, 98, 116, 0), rgba(10, 98, 116, 0.5))',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: '0px 2px 10px rgba(0,0,0,0.5)',
                fontWeight: 500,
                fontSize: { xs: '26px', sm: '36px', md: '48px' },
                zIndex: 1,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  padding: '1px',
                  borderRadius: '50rem',
                  background: 'linear-gradient(90deg, #0ff, rgba(10, 98, 116, 0))',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  zIndex: -1,
                },
              }}>
                Perspectivas clave
              </Box> para tu toma de decisiones
            </Box>
          }
          subtitle="Explora nuestras publicaciones"
          subtitlePosition="top"
          variant="centered"
        />

        {/* Caja de texto con efecto blur - Siempre visible */}
        <Box ref={blurRef} sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '1105px',
          height: 'auto',
          mt: { xs: '-25px', sm: '60px', md: '-30px' },
          mx: 'auto',
          borderRadius: '20px',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 0 10px rgba(0,0,0,0.5)',
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 3, sm: 4, md: 5 },
          zIndex: 2,
        }}>
          <Typography sx={{
            fontSize: { xs: '16px', sm: '18px', md: '20px' },
            lineHeight: 1.6,
            color: '#D4A76A',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
          }}>
            Bienvenido a nuestro blog, donde <Box component="span" sx={{ fontWeight: 700 }}>
              <strong>compartimos conocimientos, estrategias y perspectivas clave para la</strong>
            </Box>{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              gestión efectiva de tu familia, empresa y patrimonio.
            </Box>{' '}
            Aquí encontrarás artículos actualizados y expertos análisis que te ayudarán a{' '}
            <strong> tomar decisiones que fortalezcan tu legado familiar y empresarial.</strong>
          </Typography>
        </Box>

        {/* Sección de categorías */}
        <Box sx={{
          width: '100vw',
          backgroundImage: {
            xs: 'url("/bg-categorias-mobile.png")',
            md: 'url("https://res.cloudinary.com/dhoyyqszi/image/upload/v1750193681/ebm/5395_wugjdu.png")',
          },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          mt: { xs: '-130px', sm: '-160px', md: '-180px' },
          px: { xs: 2, md: 6 },
          pt: { xs: 8, sm: 10, md: 12 },
          pb: { xs: 10, sm: 12, md: 14 },
        }}>
          <Box sx={{
            maxWidth: '1213px',
            mx: 'auto',
            textAlign: 'center',
            zIndex: 1,
            position: 'relative',
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 'bold',
              fontSize: { xs: '24px', sm: '28px', md: '30px' },
              color: '#DADADA',
              mb: 1,
              mt: '150px',
            }}>
              Navega por nuestras principales categorías
            </Typography>

            <Typography variant="body2" sx={{
              color: '#DADADA',
              mb: 1,
              fontWeight: 'bold',
              fontSize: '16px',
            }}>
              ¡Encuentra fácilmente la información que necesitas!
            </Typography>

            <Typography variant="body2" sx={{
              color: '#DADADA',
              mb: 4,
              fontSize: '16px',
              maxWidth: '800px',
              margin: '0 auto',
            }}>
              Conoce nuestras tres categorías principales y descubre contenido relevante para ti.
            </Typography>

            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: { xs: 2, sm: 4, md: '60px' },
              mt: 3,
            }}>
              {['Familia', 'Empresa', 'Propiedad'].map((text) => (
                <Box key={text} sx={{
                  width: { xs: '100%', sm: '160px', md: '181px' },
                  borderRadius: '50px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  boxShadow: '0 0 6px rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Button
                    key={text}
                    onClick={() => {
                      setFilterCategory(text);
                      seccionArticulosRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    fullWidth
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(90deg, #0A6274 0%, #13B8DA 100%)',
                      color: 'white',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: '14px', sm: '16px' },
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderRadius: '50px',
                      px: '15px',
                      py: '12px',
                      '&:hover': {
                        opacity: 0.9,
                        background: 'linear-gradient(90deg, #0A6274 0%, #13B8DA 100%)',
                      },
                    }}
                  >
                    {text}
                  </Button>

                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ backgroundColor: 'white', py: { xs: 4, md: 6 } }}>
          <Box sx={{
            position: 'relative',
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 0 }
          }}>
            {/* Imagen con diseño original */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}>

            </Box>

            {/* Cinta superior y caja de texto dinámica */}
            <FeaturedPost />

          </Box>

          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            minHeight: { xs: 'auto', md: '280px' },
            position: 'relative',
            overflow: 'hidden',
            mt: '69px',
          }}>
            <Box sx={{
              flex: 1,
              backgroundImage: 'url("https://res.cloudinary.com/dhoyyqszi/image/upload/v1750193734/ebm/insightsMantenteInformado_mgeeds.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'rgba(10, 98, 116, 0.85)',
              p: { xs: 3, sm: 4, md: 5 },
              mr: { xs: 0, md: '-20px' },
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'white',
              borderBottomRightRadius: { xs: 0, md: '20px' },
            }}>
              <Typography variant="h6" sx={{
                fontSize: { xs: '22px', sm: '26px', md: '28px' },
                fontWeight: 700,
                color: '#D4A76A',
                mb: 1,
              }}>
                ¡Mantente informado!
              </Typography>
              <Typography variant="body2" sx={{
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                fontWeight: 300,
                maxWidth: '90%',
                color: 'white',
              }}>
                Suscríbete a nuestro boletín para recibir las últimas actualizaciones y artículos directamente en tu correo.
              </Typography>
            </Box>

            <NewsletterForm />
          </Box>
        </Box>

        {/* Carrusel con artículos de WordPress */}
        <Box sx={{
          backgroundColor: 'white',
          pb: 10,
          pt: 8,
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Typography sx={{
            py: 4,
            color: '#0A6274',
            fontSize: { xs: '1.6rem', md: '2rem' },
            fontWeight: 600,
            textAlign: 'center'
          }}>
            Explora nuestros <b>últimos insights</b>
          </Typography>

          {isLoadingArticles ? (
            <Box sx={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress color="primary" />
            </Box>
          ) : wordpressArticles.length > 0 ? (
            <CarouselArticulos wpData={wordpressArticles} />
          ) : (
            <Typography>No hay artículos disponibles</Typography>
          )}
        </Box>

        {/* Listado completo de artículos */}
        <Box
          ref={seccionArticulosRef}
          sx={{
            backgroundColor: '#F9F9F9',
            minHeight: '300px'
          }}
        >

          {isLoadingArticles ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              gap: 2
            }}>
              <CircularProgress color="primary" />
              <Typography variant="body1" color="textSecondary">
                Cargando artículos...
              </Typography>
            </Box>
          ) : (
            <AllArticles
              articulos={filteredArticles}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          )}
        </Box>

        {/* Banner Hub */}
        <Box sx={{
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.94)',
          py: 10,
          px: { xs: 2, md: 10 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <BannerHub />
        </Box>
      </Container>
    </>
  );
}